import { Injectable } from '@angular/core';
import { BookingModel, LocationModel, StayType } from '@app/core/models';

import { createStore, setProps, withProps } from '@ngneat/elf';
import {
  deleteAllEntities,
  getActiveEntity,
  selectActiveEntity,
  selectAllEntities,
  setActiveId,
  upsertEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { joinRequestResult } from '@ngneat/elf-requests';
import { isAfter, isBefore } from 'date-fns';
import { Observable } from 'rxjs';
import { BookingRepository } from './booking.repository';

export interface LocationsProps {
  meta: {
    lastRefresh: Date | null;
  };
}

const store = createStore(
  { name: 'locations' },
  withProps<LocationsProps>({
    meta: { lastRefresh: null },
  }),
  withEntities<LocationModel, '_id'>({ idKey: '_id' }),
  withActiveId(),
);

@Injectable({ providedIn: 'root' })
export class LocationRepository {
  constructor(private _bookingRepo: BookingRepository) {}

  locations$ = store.pipe(
    selectAllEntities(),
    joinRequestResult(['locations']),
  );
  activeLocation$: Observable<LocationModel | undefined> =
    store.pipe(selectActiveEntity());

  get activeLocation() {
    return store.query(getActiveEntity());
  }

  setActiveLocationId(locationId: string | null) {
    store.update(setActiveId(locationId));
  }

  setActiveLocation(location: LocationModel | undefined) {
    if (location) {
      store.update(upsertEntities([location]));
      if (location.bookings && location.bookings.length > 0) {
        const activeBookingId = this.getActiveBookingIdFromBookingList(
          location.bookings,
        );
        this._bookingRepo.setActiveBookingId(activeBookingId);
      } else {
        this._bookingRepo.setActiveBookingId(null);
      }
      store.update(setActiveId(location._id));
    }
  }

  upsertLocations(locations: LocationModel[]) {
    if (locations && locations.length > 0) {
      store.update(upsertEntities(locations));
    }
  }

  getActiveBookingIdFromBookingList(bookings: BookingModel[]): string | null {
    let currentBookingId = null;
    if (bookings && bookings.length > 0) {
      const residentialBookings = bookings.filter(
        booking => booking.stayType === StayType.RESIDENTIAL,
      );
      if (residentialBookings.length > 0) {
        currentBookingId = residentialBookings[0]._id;
      } else {
        const currentBookings = bookings.filter(
          booking =>
            isAfter(new Date(), new Date(booking.start)) &&
            isBefore(new Date(), new Date(booking.end)),
        );

        if (currentBookings.length > 0) {
          currentBookingId = currentBookings[0]._id;
        } else {
          const futureBookings = bookings.filter(booking =>
            isBefore(new Date(), new Date(booking.end)),
          );
          if (futureBookings.length > 0) {
            currentBookingId = futureBookings.sort(
              (a, b) =>
                new Date(a.start).getTime() - new Date(b.start).getTime(),
            )[0]._id;
          } else {
            const pastBookings = bookings.filter(booking =>
              isAfter(new Date(), new Date(booking.end)),
            );
            if (pastBookings.length > 0) {
              currentBookingId = pastBookings.sort(
                (a, b) => new Date(b.end).getTime() - new Date(a.end).getTime(),
              )[0]._id;
            } else {
              currentBookingId = bookings[0]._id;
            }
          }
        }
      }
    }
    return currentBookingId;
  }

  addLocation(location: LocationModel) {
    if (location) {
      store.update(upsertEntities([location]));
      if (location.bookings && location.bookings.length > 0) {
        const activeBookingId = this.getActiveBookingIdFromBookingList(
          location.bookings,
        );
        this._bookingRepo.setActiveBookingId(activeBookingId);
      } else {
        this._bookingRepo.setActiveBookingId(null);
      }
      store.update(setActiveId(location._id));
    }
  }

  getActiveLocation(): LocationModel | undefined {
    return store.query(getActiveEntity());
  }

  public clear(): void {
    store.update(deleteAllEntities());
    store.update(setProps({ meta: { lastRefresh: null } }));
  }
}
